import { useEffect, useRef } from "react";
import { sendVideoMeetingHeartbeat } from "../utils/sendVideoMeetingHeartbeat";
import type { VideoRoomHeartbeatResponse } from "../videoMeeting.types";

const HEARTBEAT_INTERVAL_MS = 10_000; // 10 seconds

export type UseVideoMeetingHeartbeatArgs = {
  meetingId: string | null;
  userName: string | null;
  user: { id: number } | null;
  isEnabled?: boolean;
  onHeartbeat?: (response: VideoRoomHeartbeatResponse) => void;
};

export function useVideoMeetingHeartbeat({
  meetingId,
  userName,
  user,
  isEnabled = true,
  onHeartbeat,
}: UseVideoMeetingHeartbeatArgs) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isEnabled || !meetingId || !userName) {
      return;
    }

    const sendHeartbeat = async () => {
      try {
        const response = await sendVideoMeetingHeartbeat(
          meetingId,
          userName,
          user
        );
        onHeartbeat?.(response);
      } catch {
        // Don't fail the meeting if heartbeat fails
      }
    };

    // Send immediately, then on interval
    sendHeartbeat();
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isEnabled, meetingId, userName, user, onHeartbeat]);
}
